import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MapExplorer = () => {
  const [selectedRegion, setSelectedRegion] = useState('asia');
  const [hoveredDestination, setHoveredDestination] = useState(null);
  const navigate = useNavigate();

  const regions = [
    { id: 'asia', label: 'Asia', icon: 'Globe' },
    { id: 'europe', label: 'Europe', icon: 'Building' },
    { id: 'americas', label: 'Americas', icon: 'Mountain' },
    { id: 'africa', label: 'Africa', icon: 'Trees' },
    { id: 'oceania', label: 'Oceania', icon: 'Waves' }
  ];

  const destinationsByRegion = {
    asia: [
      {
        id: 1,
        name: "Bali, Indonesia",
        coordinates: { lat: -8.3405, lng: 115.0920 },
        tours: 45,
        startingPrice: 299,
        image: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=400&h=300&fit=crop",
        description: "Tropical paradise with ancient temples"
      },
      {
        id: 2,
        name: "Kyoto, Japan",
        coordinates: { lat: 35.0116, lng: 135.7681 },
        tours: 28,
        startingPrice: 380,
        image: "https://images.pixabay.com/photo-2020/05/18/16/17/cherry-blossoms-5187041_1280.jpg?w=400&h=300&fit=crop",
        description: "Ancient temples and traditional gardens"
      },
      {
        id: 3,
        name: "Bangkok, Thailand",
        coordinates: { lat: 13.7563, lng: 100.5018 },
        tours: 36,
        startingPrice: 250,
        image: "https://images.pexels.com/photos/1007426/pexels-photo-1007426.jpeg?w=400&h=300&fit=crop",
        description: "Vibrant street life and golden temples"
      }
    ],
    europe: [
      {
        id: 4,
        name: "Santorini, Greece",
        coordinates: { lat: 36.3932, lng: 25.4615 },
        tours: 32,
        startingPrice: 450,
        image: "https://images.pexels.com/photos/161901/santorini-greece-island-161901.jpeg?w=400&h=300&fit=crop",
        description: "Iconic white buildings and stunning sunsets"
      },
      {
        id: 5,
        name: "Paris, France",
        coordinates: { lat: 48.8566, lng: 2.3522 },
        tours: 67,
        startingPrice: 275,
        image: "https://images.pexels.com/photos/338515/pexels-photo-338515.jpeg?w=400&h=300&fit=crop",
        description: "City of lights and romance"
      },
      {
        id: 6,
        name: "Rome, Italy",
        coordinates: { lat: 41.9028, lng: 12.4964 },
        tours: 54,
        startingPrice: 320,
        image: "https://images.pixabay.com/photo/2018/11/06/17/04/rome-3798433_1280.jpg?w=400&h=300&fit=crop",
        description: "Ancient history and incredible cuisine"
      }
    ],
    americas: [
      {
        id: 7,
        name: "Machu Picchu, Peru",
        coordinates: { lat: -13.1631, lng: -72.5450 },
        tours: 18,
        startingPrice: 520,
        image: "https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=400&h=300&fit=crop",
        description: "Ancient Incan citadel in the clouds"
      },
      {
        id: 8,
        name: "New York, USA",
        coordinates: { lat: 40.7128, lng: -74.0060 },
        tours: 89,
        startingPrice: 180,
        image: "https://images.pexels.com/photos/290386/pexels-photo-290386.jpeg?w=400&h=300&fit=crop",
        description: "The city that never sleeps"
      }
    ],
    africa: [
      {
        id: 9,
        name: "Marrakech, Morocco",
        coordinates: { lat: 31.6295, lng: -7.9811 },
        tours: 23,
        startingPrice: 350,
        image: "https://images.pixabay.com/photo/2017/12/15/13/51/polynesia-3021072_1280.jpg?w=400&h=300&fit=crop",
        description: "Vibrant souks and desert adventures"
      }
    ],
    oceania: [
      {
        id: 10,
        name: "Sydney, Australia",
        coordinates: { lat: -33.8688, lng: 151.2093 },
        tours: 41,
        startingPrice: 420,
        image: "https://images.pexels.com/photos/1878293/pexels-photo-1878293.jpeg?w=400&h=300&fit=crop",
        description: "Iconic harbor and stunning beaches"
      }
    ]
  };

  const currentDestinations = destinationsByRegion?.[selectedRegion] || [];

  const handleDestinationClick = (destination) => {
    navigate(`/search-results?destination=${encodeURIComponent(destination?.name)}`);
  };

  const handleExploreAllClick = () => {
    navigate('/search-results');
  };

  return (
    <section className="py-16 lg:py-24 bg-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Explore the World
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover amazing destinations across the globe with our interactive map explorer
          </p>
        </div>

        {/* Region Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {regions?.map((region) => (
            <button
              key={region?.id}
              onClick={() => setSelectedRegion(region?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedRegion === region?.id
                  ? 'bg-primary text-white shadow-lg scale-105'
                  : 'bg-white text-muted-foreground hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              <Icon name={region?.icon} size={18} />
              <span>{region?.label}</span>
            </button>
          ))}
        </div>

        {/* Map Container */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[500px]">
            {/* Map Section */}
            <div className="lg:col-span-2 relative bg-gradient-to-br from-blue-50 to-blue-100 p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 rounded-full mb-6">
                  <Icon name="Map" size={48} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  Interactive Map Coming Soon
                </h3>
                <p className="text-muted-foreground mb-6">
                  We're building an amazing interactive map experience to help you explore destinations visually
                </p>
                
                {/* Mock Map Placeholder */}
                <div className="w-full h-64 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Globe" size={64} className="text-primary/40 mx-auto mb-4" />
                    <p className="text-primary/60 font-medium">World Map Visualization</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Destinations List */}
            <div className="bg-muted/30 p-6 overflow-y-auto max-h-[500px]">
              <h3 className="text-xl font-bold text-foreground mb-6 sticky top-0 bg-muted/30 pb-2">
                {regions?.find(r => r?.id === selectedRegion)?.label} Destinations
              </h3>
              
              <div className="space-y-4">
                {currentDestinations?.map((destination) => (
                  <div
                    key={destination?.id}
                    className="bg-white rounded-xl p-4 cursor-pointer hover:shadow-lg transition-all duration-200 hover-lift"
                    onClick={() => handleDestinationClick(destination)}
                    onMouseEnter={() => setHoveredDestination(destination?.id)}
                    onMouseLeave={() => setHoveredDestination(null)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={destination?.image}
                          alt={destination?.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/images/no_image.png';
                          }}
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-foreground truncate">
                          {destination?.name}
                        </h4>
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {destination?.description}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                            <Icon name="MapPin" size={14} />
                            <span>{destination?.tours} tours</span>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-sm text-muted-foreground">From</div>
                            <div className="font-bold text-primary">${destination?.startingPrice}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className={`transition-transform duration-200 ${
                        hoveredDestination === destination?.id ? 'translate-x-0' : 'translate-x-2'
                      }`}>
                        <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {currentDestinations?.length === 0 && (
                <div className="text-center py-8">
                  <Icon name="MapPin" size={48} className="text-muted-foreground/40 mx-auto mb-4" />
                  <p className="text-muted-foreground">No destinations available for this region yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Button
            size="lg"
            onClick={handleExploreAllClick}
            iconName="Compass"
            iconPosition="left"
            className="px-8 py-4"
          >
            Explore All Destinations
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MapExplorer;